import { useNavigate } from "react-router-dom";
import { Clock, ExternalLink, FileText, Info, MessageSquare, Phone, Users, Zap } from "lucide-react";
import { truncateText } from "@/components/agent/display/utils/formatters";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Agent } from "@/types/agent/agent";
import { formatDuration, formatLanguage, formatSpeed } from "@/utils/formatting/general";

interface SimpleOverviewProps {
  genie: Agent;
  isOpen: boolean;
  onClose: () => void;
}

export const SimpleOverview = ({ genie, isOpen, onClose }: SimpleOverviewProps) => {
  const navigate = useNavigate();
  const config = genie?.settings?.conversation_config;

  if (!genie || !config) return null;

  const knowledgeBaseCount = config.agent?.prompt?.knowledge_base?.length || 0;
  const phoneEnabled = Boolean(genie.phone);
  const teamMemberCount = genie?.report_recipients?.length || 0;
  const transcriptsEnabled = Array.isArray(config?.conversation?.client_events)
    ? config.conversation.client_events.includes("user_transcript")
    : false;

  return (
    <CoreDrawer isOpen={isOpen} onClose={onClose} title={genie.name} description="Quick Overview">
      <div className="py-2 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Basic Information */}
          <div className="space-y-3 border border-gray-300 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Info className="h-4 w-4" />
              <span>Basic Information</span>
            </div>
            <div className="space-y-2">
              {genie.category && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Category</span>
                  <span className="text-sm font-medium capitalize">{genie.category}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Status</span>
                <Badge variant={genie.is_active ? "default" : "secondary"}>
                  {genie.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Visibility</span>
                <Badge variant={genie.is_public ? "default" : "outline"}>
                  {genie.is_public ? "Public" : "Private"}
                </Badge>
              </div>
              {genie.description && (
                <div className="space-y-1">
                  <span className="text-sm text-gray-500">Description</span>
                  <p className="text-sm">{truncateText(genie.description, 200)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Voice & Language */}
          <div className="space-y-3 border border-gray-300 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <MessageSquare className="h-4 w-4" />
              <span>Voice & Language</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Voice</span>
                <span className="text-sm font-medium">{genie.voice_label || "Default"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Language</span>
                <span className="text-sm font-medium">{formatLanguage(config.agent?.language || "en")}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Speech Speed</span>
                <span className="text-sm font-medium">{formatSpeed(config.tts?.speed || 1)}</span>
              </div>
              {config.agent?.first_message && (
                <div className="space-y-1">
                  <span className="text-sm text-gray-500">First Message</span>
                  <p className="text-sm bg-gray-50 rounded-md p-2 italic line-clamp-4">
                    "{truncateText(config.agent.first_message, 300)}"
                  </p>
                </div>
              )}
              {config.agent?.prompt?.prompt && (
                <div className="space-y-1">
                  <span className="text-sm text-gray-500">System Prompt</span>
                  <p className="text-sm bg-gray-50 rounded-md p-2 line-clamp-6">
                    {truncateText(config.agent.prompt.prompt, 400)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Conversation Limits */}
          <div className="space-y-3 border border-gray-300 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Clock className="h-4 w-4" />
              <span>Conversation Limits</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Max Duration</span>
                <span className="text-sm font-medium">
                  {formatDuration(config.conversation?.max_duration_seconds || 600)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Silence Before End</span>
                <span className="text-sm font-medium">
                  {config.turn?.silence_end_call_timeout === -1
                    ? "Off"
                    : `${config.turn?.silence_end_call_timeout || 300} seconds`}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Transcripts</span>
                <Badge variant={transcriptsEnabled ? "default" : "outline"} className="text-xs">
                  {transcriptsEnabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Daily Call Limit</span>
                <span className="text-sm font-medium">{genie.call_limit ?? "Not set"}</span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="space-y-3 border border-gray-300 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Zap className="h-4 w-4" />
              <span>Quick Stats</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Knowledge Base
                </span>
                <span className="text-sm font-medium">
                  {knowledgeBaseCount} {knowledgeBaseCount === 1 ? "document" : "documents"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  Phone Integration
                </span>
                <Badge variant={phoneEnabled ? "default" : "outline"} className="text-xs">
                  {phoneEnabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  Support Team
                </span>
                <span className="text-sm font-medium">
                  {teamMemberCount} {teamMemberCount === 1 ? "member" : "members"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" />
                  Redirect After Call
                </span>
                <Badge variant={genie.redirect_url ? "default" : "outline"} className="text-xs">
                  {genie.redirect_url ? "Enabled" : "Disabled"}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-2 pb-4">
          <Button
            onClick={() => {
              onClose();
              navigate(`/genie-hub/my-genies/${genie.id}`);
            }}
            variant="outline"
          >
            View Full Configuration
          </Button>
        </div>
      </div>
    </CoreDrawer>
  );
};
